import React, { useEffect, useMemo, useRef, useState } from "react";
import { extractQuote, draftQuoteText, saveQuote } from "../api/quotes";
import { formatCurrency } from "../utils/currency";
import { supabase } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";
import {
  FileText,
  Image as ImageIcon,
  Sparkles,
  Copy,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Filter,
  User,
  Plus,
  X,
} from "lucide-react";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const MAX_AI_IMAGES = 4;

async function calculateQuoteAPI(parsedData) {
  const response = await fetch(`${API_BASE_URL}/api/calculate-quote`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(parsedData),
  });

  const rawText = await response.text();
  console.log("RAW /api/calculate-quote RESPONSE:", rawText);

  let data = null;

  try {
    data = rawText ? JSON.parse(rawText) : null;
  } catch {
    console.error("Backend returned non-JSON:", rawText);
    throw new Error("Backend returned invalid JSON. Check server terminal.");
  }

  if (!response.ok) {
    throw new Error(data?.error || "Failed to calculate quote");
  }

  return data;
}

function formatNumber(value) {
  return Number(value || 0).toFixed(2);
}

function formatPercent(value) {
  return `${Math.round(Number(value || 0) * 100)}%`;
}

function isAiAdjusted(row) {
  return !!(row?.flags?.aiAdjusted || row?.aiAdjusted);
}

function getRowQuantity(row) {
  return Number(row?.quantity ?? row?.qty ?? row?.hours ?? 0);
}

function getRowUnitPrice(row) {
  return Number(row?.unitPrice ?? row?.hourlyRate ?? row?.price ?? row?.rate ?? 0);
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load image"));
    };

    img.src = url;
  });
}

async function compressImage(file, options = {}) {
  const {
    maxWidth = 1600,
    maxHeight = 1600,
    quality = 0.8,
    outputType = "image/jpeg",
  } = options;

  if (!file.type.startsWith("image/")) return file;
  if (file.type === "image/gif") return file;

  const img = await loadImage(file);

  let { width, height } = img;
  const ratio = Math.min(maxWidth / width, maxHeight / height, 1);

  const targetWidth = Math.round(width * ratio);
  const targetHeight = Math.round(height * ratio);

  const canvas = document.createElement("canvas");
  canvas.width = targetWidth;
  canvas.height = targetHeight;

  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

  const blob = await new Promise((resolve) =>
    canvas.toBlob(resolve, outputType, quality)
  );

  if (!blob) return file;

  const extension = outputType === "image/png" ? "png" : "jpg";
  const baseName = file.name.replace(/\.[^.]+$/, "");

  return new File([blob], `${baseName}-compressed.${extension}`, {
    type: outputType,
    lastModified: Date.now(),
  });
}

function hasFallback(parsed) {
  const assumptions = parsed?.assumptions || [];
  const reviewFlags = parsed?.reviewFlags || [];

  return (
    assumptions.some((x) => String(x).toLowerCase().includes("fallback")) ||
    reviewFlags.some((x) => String(x).toLowerCase().includes("ai unavailable"))
  );
}

function isImageAssisted(parsed) {
  const assumptions = parsed?.assumptions || [];
  return assumptions.some((x) =>
    String(x).toLowerCase().includes("image-assisted")
  );
}

function getOverallVisionConfidence(meta) {
  const overall = meta?.visionConfidence?.overall;

  if (typeof overall === "number" && Number.isFinite(overall)) {
    return overall;
  }

  const values = Object.values(meta?.visionConfidence || {}).filter(
    (value) => typeof value === "number" && Number.isFinite(value)
  );

  if (!values.length) return null;

  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function getConfidenceLevel(meta) {
  const score = getOverallVisionConfidence(meta);

  if (score === null) return "unknown";
  if (score >= 0.8) return "high";
  if (score >= 0.5) return "medium";
  return "low";
}

function getConfidenceConfig(level) {
  switch (level) {
    case "high":
      return {
        label: "High confidence",
        tone: "green",
        panelClass: "border-emerald-900 bg-emerald-950/30 text-emerald-200",
      };
    case "medium":
      return {
        label: "Medium confidence",
        tone: "yellow",
        panelClass: "border-amber-900 bg-amber-950/30 text-amber-200",
      };
    case "low":
      return {
        label: "Low confidence",
        tone: "red",
        panelClass: "border-red-900 bg-red-950/30 text-red-200",
      };
    default:
      return {
        label: "Confidence unavailable",
        tone: "default",
        panelClass: "border-gray-800 bg-gray-950/60 text-gray-300",
      };
  }
}

function getCustomerName(customer) {
  return (
    customer?.name ||
    customer?.full_name ||
    customer?.company_name ||
    customer?.email ||
    "Unnamed customer"
  );
}

function getCustomerAddress(customer) {
  return (
    customer?.address ||
    customer?.location ||
    customer?.city ||
    customer?.postal_city ||
    ""
  );
}

function createCustomerSnapshot(customer) {
  if (!customer) return null;

  return {
    id: customer.id,
    name: getCustomerName(customer),
    email: customer.email || "",
    phone: customer.phone || "",
    address: getCustomerAddress(customer),
    type: customer.type || customer.customer_type || "",
  };
}

function SectionCard({ title, icon: Icon, children, right }) {
  return (
    <section className="rounded-2xl border border-gray-800 bg-gradient-to-b from-gray-900 to-gray-950 shadow-sm">
      <div className="flex items-center justify-between gap-3 border-b border-gray-800 px-5 py-4">
        <div className="flex items-center gap-2 text-sm font-medium text-gray-300">
          {Icon && <Icon size={16} className="text-gray-500" />}
          <h2 className="text-sm font-semibold tracking-tight text-white">
            {title}
          </h2>
        </div>
        {right}
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

function MetricCard({ label, value, highlight = false }) {
  return (
    <div
      className={`rounded-xl border px-4 py-3 transition hover:scale-[1.02] ${
        highlight
          ? "border-emerald-800 bg-emerald-950/40 ring-1 ring-emerald-700"
          : "border-gray-800/60 bg-gray-950/60"
      }`}
    >
      <div
        className={`text-xs uppercase tracking-wide ${
          highlight ? "text-emerald-400/80" : "text-gray-500"
        }`}
      >
        {label}
      </div>
      <div
        className={`mt-1 text-base font-semibold ${
          highlight ? "text-emerald-300" : "text-gray-100"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function StatusBadge({ children, tone = "default" }) {
  const toneClasses = {
    default: "bg-gray-800 text-gray-300 border-gray-700",
    blue: "bg-blue-950/40 text-blue-300 border-blue-900",
    yellow: "bg-amber-950/40 text-amber-300 border-amber-900",
    red: "bg-red-950/40 text-red-300 border-red-900",
    green: "bg-emerald-950/40 text-emerald-300 border-emerald-900",
  };

  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${toneClasses[tone]}`}
    >
      {children}
    </span>
  );
}

function SelectField(props) {
  return (
    <select
      {...props}
      className="rounded-xl border border-white/10 bg-black/30 px-3 py-2.5 text-sm text-gray-100 outline-none focus:border-gray-600 disabled:cursor-not-allowed disabled:opacity-50"
    />
  );
}

function CustomerSelect({
  customers,
  selectedCustomer,
  onSelect,
  onClear,
  loading,
}) {
  return (
    <div className="flex min-w-[240px] items-center gap-2 rounded-xl border border-white/10 bg-black/30 px-3 py-2.5">
      <User size={16} className="shrink-0 text-gray-500" />

      <select
        value={selectedCustomer?.id || ""}
        onChange={(e) => {
          const customer = customers.find((item) => item.id === e.target.value);
          onSelect(customer || null);
        }}
        disabled={loading}
        className="w-full bg-transparent text-sm text-gray-100 outline-none disabled:cursor-not-allowed disabled:opacity-50"
      >
        <option value="">No customer</option>
        {customers.map((customer) => (
          <option key={customer.id} value={customer.id}>
            {getCustomerName(customer)}
          </option>
        ))}
      </select>

      {selectedCustomer && (
        <button
          type="button"
          onClick={onClear}
          disabled={loading}
          className="rounded-lg p-1 text-gray-500 hover:bg-white/10 hover:text-gray-200 disabled:opacity-50"
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
}

export default function Quotes() {
  const { workspace } = useAuth();
  const [requestText, setRequestText] = useState("");
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [parsed, setParsed] = useState(null);
  const [result, setResult] = useState(null);
  const [quoteText, setQuoteText] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessingImages, setIsProcessingImages] = useState(false);
  const [showRawJson, setShowRawJson] = useState(false);
  const [reviewModeOnly, setReviewModeOnly] = useState(false);
  const [country, setCountry] = useState("DK");
  const [currency, setCurrency] = useState("DKK");
  const [quoteLanguage, setQuoteLanguage] = useState("da");
  const [marginPercent, setMarginPercent] = useState(25);

  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const fileInputRef = useRef(null);

  const aiImages = useMemo(() => images.slice(0, MAX_AI_IMAGES), [images]);
  const hasExtraImagesForAi = images.length > MAX_AI_IMAGES;

  const selectedCurrency = useMemo(() => {
    if (parsed?.currency === "SEK" || parsed?.currency === "DKK") {
      return parsed.currency;
    }

    if (result?.meta?.currency === "SEK" || result?.meta?.currency === "DKK") {
      return result.meta.currency;
    }

    return currency === "SEK" ? "SEK" : "DKK";
  }, [parsed, result, currency]);

  const selectedQuoteLanguage = useMemo(() => {
    if (parsed?.quoteLanguage) return parsed.quoteLanguage;
    return quoteLanguage || (country === "SE" ? "sv" : "da");
  }, [parsed, quoteLanguage, country]);

  const meta = result?.meta || {};
  const confidenceScore = getOverallVisionConfidence(meta);
  const confidenceLevel = getConfidenceLevel(meta);
  const confidenceConfig = getConfidenceConfig(confidenceLevel);
  const aiReviewFlags = meta.reviewFlags || [];
  const photoScopeNotes = meta.photoScopeNotes || [];

  const aiAdjustedMaterials = (result?.materials || []).filter(isAiAdjusted).length;
  const aiAdjustedLabour = (result?.labour || []).filter(isAiAdjusted).length;
  const aiAdjustedExtras = (result?.extras || []).filter(isAiAdjusted).length;
  const totalAiAdjustedRows =
    aiAdjustedMaterials + aiAdjustedLabour + aiAdjustedExtras;

  const filteredMaterials = reviewModeOnly
    ? (result?.materials || []).filter(isAiAdjusted)
    : result?.materials || [];

  const filteredLabour = reviewModeOnly
    ? (result?.labour || []).filter(isAiAdjusted)
    : result?.labour || [];

  const filteredExtras = reviewModeOnly
    ? (result?.extras || []).filter(isAiAdjusted)
    : result?.extras || [];

  useEffect(() => {
  if (workspace?.id) {
    loadCustomers();
  }
}, [workspace?.id]);

  useEffect(() => {
    const previews = images.map((file) => ({
      file,
      url: URL.createObjectURL(file),
    }));

    setImagePreviews(previews);

    return () => {
      previews.forEach((preview) => URL.revokeObjectURL(preview.url));
    };
  }, [images]);

  async function loadCustomers() {
    try {
      setCustomersLoading(true);

      const { data, error: customersError } = await supabase
  .from("customers")
  .select("*")
  .eq("workspace_id", workspace.id)
  .order("created_at", { ascending: false });

      if (customersError) throw customersError;

      setCustomers(data || []);
    } catch (err) {
      console.error("Failed to load customers:", err);
    } finally {
      setCustomersLoading(false);
    }
  }

  function handleCustomerSelect(customer) {
    setSelectedCustomer(customer);

    if (!customer) return;

    const name = getCustomerName(customer);
    const email = customer.email || "";
    const address = getCustomerAddress(customer);

    const autofill = [
      name ? `Customer: ${name}` : "",
      email ? `Email: ${email}` : "",
      address ? `Location: ${address}` : "",
    ]
      .filter(Boolean)
      .join("\n");

    if (!autofill) return;

    setRequestText((prev) => {
      if (!prev.trim()) return `${autofill}\n\n`;

      const alreadyHasCustomer =
        prev.includes(name) || (email && prev.includes(email));

      if (alreadyHasCustomer) return prev;

      return `${autofill}\n\n${prev}`;
    });
  }

  function handleCountryChange(value) {
    setCountry(value);

    if (value === "SE") {
      setCurrency("SEK");
      if (quoteLanguage === "da") setQuoteLanguage("sv");
    } else {
      setCurrency("DKK");
      if (quoteLanguage === "sv") setQuoteLanguage("da");
    }
  }

  async function handleGenerate() {
  try {
    setLoading(true);
    setError("");
    setSaveMessage("");
    setQuoteText("");
    setInfoMessage("");
    setParsed(null);
    setResult(null);

    if (!workspace?.id) {
      throw new Error("No workspace loaded. Please reload and log in again.");
    }

    if (hasExtraImagesForAi) {
      setInfoMessage(
        `Using first ${MAX_AI_IMAGES} images for AI analysis. All ${images.length} images will still be saved with the quote.`
      );
    }

    const extracted = await extractQuote(requestText, aiImages);

    const customerSnapshot = createCustomerSnapshot(selectedCustomer);

    const parsedData = {
      ...extracted,
      country,
      currency,
      quoteLanguage,
      marginPercent,
      workspace_id: workspace.id,
      customerId: selectedCustomer?.id || null,
      customer_id: selectedCustomer?.id || null,
      customer: customerSnapshot,
      customerSnapshot,
    };

    const calcResult = await calculateQuoteAPI(parsedData);
    const quoteDraft = await draftQuoteText(parsedData, calcResult);

    const saved = await saveQuote({
      requestText,
      parsedQuote: parsedData,
      estimate: calcResult,
      quoteText: quoteDraft.quoteText || "",
      images,
      workspace_id: workspace.id,
      customerId: selectedCustomer?.id || null,
      customer_id: selectedCustomer?.id || null,
      customerSnapshot,
      customer_snapshot: customerSnapshot,
    });

      setParsed(parsedData);
      setResult(calcResult);
      setQuoteText(quoteDraft.quoteText || "");
      setSaveMessage(`Saved quote: ${saved.id}`);
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  }

  async function addFiles(fileList) {
    const files = Array.from(fileList || []).filter((file) =>
      file.type.startsWith("image/")
    );

    if (!files.length) return;

    try {
      setIsProcessingImages(true);
      setError("");
      setInfoMessage("");

      const compressedFiles = await Promise.all(
        files.map((file) =>
          compressImage(file, {
            maxWidth: 1600,
            maxHeight: 1600,
            quality: 0.8,
            outputType: "image/jpeg",
          })
        )
      );

      setImages((prev) => [...prev, ...compressedFiles]);
    } catch (err) {
      setError(err.message || "Failed to process images");
    } finally {
      setIsProcessingImages(false);
    }
  }

  async function handleImageChange(e) {
    await addFiles(e.target.files);
    e.target.value = "";
  }

  function removeImage(indexToRemove) {
    setImages((prev) => prev.filter((_, index) => index !== indexToRemove));
  }

  function clearImages() {
    setImages([]);
    setInfoMessage("");
  }

  function openFilePicker() {
    fileInputRef.current?.click();
  }

  function handleDragEnter(e) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }

  function handleDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }

  function handleDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }

  async function handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    await addFiles(e.dataTransfer.files);
  }

  return (
    <div className="min-h-screen bg-gray-950 text-gray-200">
      <div className="mx-auto flex min-h-[calc(100vh-80px)] max-w-5xl flex-col items-center justify-center px-6 py-12">
        <div className="mb-8 text-center">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs text-gray-400">
            <Sparkles size={14} />
            AI-assisted construction quotes
          </div>

          <h1 className="text-4xl font-semibold tracking-tight text-white">
            What should we quote?
          </h1>

          <p className="mt-3 text-sm text-gray-400">
            Describe the project, add photos, choose settings, and generate a quote.
          </p>
        </div>

        {(error || infoMessage || saveMessage) && (
          <div className="mb-4 w-full space-y-2">
            {error && (
              <div className="rounded-xl border border-red-900 bg-red-950/40 px-4 py-3 text-sm text-red-300">
                {error}
              </div>
            )}

            {infoMessage && (
              <div className="rounded-xl border border-blue-900 bg-blue-950/40 px-4 py-3 text-sm text-blue-300">
                {infoMessage}
              </div>
            )}

            {saveMessage && (
              <div className="rounded-xl border border-emerald-900 bg-emerald-950/40 px-4 py-3 text-sm text-emerald-300">
                {saveMessage}
              </div>
            )}
          </div>
        )}

        <div className="w-full rounded-[28px] border border-white/10 bg-white/[0.04] p-4 shadow-2xl backdrop-blur">
          <textarea
            value={requestText}
            onChange={(e) => setRequestText(e.target.value)}
            placeholder="Describe the customer request..."
            rows={8}
            className="min-h-[210px] w-full resize-none rounded-2xl border border-white/10 bg-black/30 px-5 py-4 text-sm leading-6 text-white outline-none placeholder:text-gray-500 focus:border-gray-600 focus:ring-1 focus:ring-gray-700"
          />

          {selectedCustomer && (
            <div className="mt-3 flex flex-wrap items-center gap-2 rounded-2xl border border-emerald-900 bg-emerald-950/20 px-4 py-3 text-sm text-emerald-200">
              <User size={16} />
              <span className="font-medium">{getCustomerName(selectedCustomer)}</span>
              {selectedCustomer.email && (
                <span className="text-emerald-300/70">{selectedCustomer.email}</span>
              )}
              {getCustomerAddress(selectedCustomer) && (
                <span className="text-emerald-300/70">
                  {getCustomerAddress(selectedCustomer)}
                </span>
              )}
            </div>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-white/10 pt-4">
            <CustomerSelect
              customers={customers}
              selectedCustomer={selectedCustomer}
              onSelect={handleCustomerSelect}
              onClear={() => setSelectedCustomer(null)}
              loading={loading || customersLoading}
            />

            <SelectField
              value={country}
              onChange={(e) => handleCountryChange(e.target.value)}
              disabled={loading}
            >
              <option value="DK">DK</option>
              <option value="SE">SE</option>
            </SelectField>

            <SelectField
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              disabled={loading}
            >
              <option value="DKK">DKK</option>
              <option value="SEK">SEK</option>
            </SelectField>

            <SelectField
              value={selectedQuoteLanguage}
              onChange={(e) => setQuoteLanguage(e.target.value)}
              disabled={loading}
            >
              <option value="da">Danish</option>
              <option value="sv">Swedish</option>
              <option value="en">English</option>
            </SelectField>

            <SelectField
              value={String(marginPercent)}
              onChange={(e) => setMarginPercent(Number(e.target.value))}
              disabled={loading}
            >
              <option value="15">15%</option>
              <option value="20">20%</option>
              <option value="25">25%</option>
              <option value="30">30%</option>
            </SelectField>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageChange}
              className="hidden"
            />

            <button
              type="button"
              onClick={openFilePicker}
              disabled={loading || isProcessingImages}
              className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-2.5 text-sm font-medium text-gray-200 hover:bg-white/[0.08] disabled:opacity-50"
            >
              <ImageIcon size={16} />
              Photos
              {images.length > 0 && (
                <span className="rounded-full bg-white/10 px-2 py-0.5 text-xs">
                  {images.length}
                </span>
              )}
            </button>

            <button
              type="button"
              onClick={() => window.location.assign("/customers")}
              className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-2.5 text-sm font-medium text-gray-200 hover:bg-white/[0.08]"
            >
              <Plus size={16} />
              Customer
            </button>

            <button
              type="button"
              onClick={handleGenerate}
              disabled={!requestText.trim() || loading || isProcessingImages}
              className="ml-auto inline-flex items-center gap-2 rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-gray-950 hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Sparkles size={16} />
              {loading ? "Generating..." : "Generate quote"}
            </button>
          </div>

          <div
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={[
              "mt-4 rounded-2xl border border-dashed px-4 py-4 text-center text-sm transition",
              isDragging
                ? "border-gray-500 bg-white/[0.06] text-white"
                : "border-white/10 bg-black/20 text-gray-500",
            ].join(" ")}
          >
            Drag photos here, or click Photos above.
          </div>

          {isProcessingImages && (
            <div className="mt-3 text-sm text-gray-400">Processing images...</div>
          )}

          {!!images.length && (
            <div className="mt-4 space-y-4">
              {hasExtraImagesForAi && (
                <div className="rounded-xl border border-blue-900 bg-blue-950/40 px-4 py-3 text-sm text-blue-300">
                  AI will use the first {MAX_AI_IMAGES} images. All {images.length} images will still be saved with the quote.
                </div>
              )}

              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                {imagePreviews.map((item, index) => (
                  <div
                    key={`${item.file.name}-${index}`}
                    className="overflow-hidden rounded-xl border border-white/10 bg-black/30"
                  >
                    <div className="h-28 bg-gray-900">
                      <img
                        src={item.url}
                        alt={item.file.name}
                        className="h-full w-full object-cover"
                      />
                    </div>

                    <div className="p-2">
                      <div className="truncate text-xs text-gray-400">
                        {item.file.name}
                      </div>

                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="mt-2 w-full rounded-lg border border-white/10 px-2 py-1.5 text-xs text-gray-300 hover:bg-white/[0.06]"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={clearImages}
                className="rounded-lg border border-white/10 px-3 py-2 text-sm font-medium text-gray-300 hover:bg-white/[0.06]"
              >
                Clear all photos
              </button>
            </div>
          )}
        </div>
      </div>

      {(parsed || result || quoteText) && (
        <div className="border-t border-white/10 bg-gray-950 px-6 py-6">
          <div className="mx-auto max-w-7xl space-y-6">
            {parsed && (
              <SectionCard title="AI Insights" icon={Sparkles}>
                <div className="mb-4 flex flex-wrap items-center gap-2">
                  {selectedCustomer && (
                    <StatusBadge tone="green">
                      {getCustomerName(selectedCustomer)}
                    </StatusBadge>
                  )}

                  {isImageAssisted(parsed) && (
                    <StatusBadge tone="blue">Image-assisted</StatusBadge>
                  )}

                  {parsed.requiresManualReview && (
                    <StatusBadge tone="yellow">Manual review required</StatusBadge>
                  )}

                  {hasFallback(parsed) && (
                    <StatusBadge tone="red">Fallback used</StatusBadge>
                  )}

                  {!parsed.requiresManualReview && !hasFallback(parsed) && (
                    <StatusBadge tone="green">Ready for review</StatusBadge>
                  )}

                  <StatusBadge tone="default">{selectedCurrency}</StatusBadge>

                  <StatusBadge tone="default">
                    {selectedQuoteLanguage === "da"
                      ? "Danish"
                      : selectedQuoteLanguage === "sv"
                      ? "Swedish"
                      : "English"}
                  </StatusBadge>

                  <StatusBadge tone="default">{marginPercent}% margin</StatusBadge>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="rounded-xl border border-gray-800 bg-gray-950/60 p-4">
                    <div className="mb-2 text-sm font-medium text-white">
                      Assumptions
                    </div>

                    {!!parsed.assumptions?.length ? (
                      <ul className="space-y-2 pl-4 text-sm text-gray-400">
                        {parsed.assumptions.map((item, index) => (
                          <li key={index} className="list-disc">
                            {item}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="text-sm text-gray-500">No assumptions.</div>
                    )}
                  </div>

                  <div className="rounded-xl border border-gray-800 bg-gray-950/60 p-4">
                    <div className="mb-2 text-sm font-medium text-white">
                      Review flags
                    </div>

                    {!!parsed.reviewFlags?.length ? (
                      <ul className="space-y-2 pl-4 text-sm text-amber-300">
                        {parsed.reviewFlags.map((item, index) => (
                          <li key={index} className="list-disc">
                            {item}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="text-sm text-gray-500">No review flags.</div>
                    )}
                  </div>
                </div>
              </SectionCard>
            )}

            {parsed && (
              <SectionCard
                title="AI Interpretation"
                right={
                  <button
                    type="button"
                    onClick={() => setShowRawJson((prev) => !prev)}
                    className="inline-flex items-center gap-2 rounded-lg border border-gray-700 bg-gray-900 px-3 py-2 text-sm text-gray-200 transition hover:bg-gray-800"
                  >
                    {showRawJson ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    {showRawJson ? "Hide Raw JSON" : "Show Raw JSON"}
                  </button>
                }
              >
                {showRawJson ? (
                  <pre className="overflow-x-auto rounded-xl border border-gray-800 bg-gray-950 p-4 text-xs text-gray-300">
                    {JSON.stringify(parsed, null, 2)}
                  </pre>
                ) : (
                  <div className="text-sm text-gray-500">
                    Raw AI JSON is hidden by default.
                  </div>
                )}
              </SectionCard>
            )}

            {result && (
              <SectionCard
                title="Estimate Result"
                icon={Sparkles}
                right={
                  <button
                    type="button"
                    onClick={() => setReviewModeOnly((prev) => !prev)}
                    className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition ${
                      reviewModeOnly
                        ? "border-amber-800 bg-amber-950/40 text-amber-200"
                        : "border-gray-700 bg-gray-900 text-gray-200 hover:bg-gray-800"
                    }`}
                  >
                    <Filter size={16} />
                    {reviewModeOnly ? "Review Mode On" : "Review Mode Off"}
                  </button>
                }
              >
                <div className="space-y-6">
                  <div className={`rounded-2xl border p-4 ${confidenceConfig.panelClass}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <StatusBadge tone={confidenceConfig.tone}>
                            {confidenceConfig.label}
                          </StatusBadge>

                          {confidenceLevel === "low" && (
                            <StatusBadge tone="red">Needs review</StatusBadge>
                          )}

                          <StatusBadge tone="default">{selectedCurrency}</StatusBadge>

                          <StatusBadge tone="default">
                            {Number(result.summary?.marginPercent ?? marginPercent)}% margin
                          </StatusBadge>
                        </div>

                        <div className="mt-3 text-sm text-gray-300">
                          {images.length
                            ? "AI confidence for the photo-based interpretation and pricing adjustments."
                            : "No photo analysis was used. This quote was generated from text input and calculator rules."}
                        </div>
                      </div>

                      <div className="rounded-xl border border-gray-800 bg-gray-950/60 px-4 py-3 text-right">
                        <div className="text-xs uppercase tracking-wide text-gray-500">
                          Vision confidence
                        </div>
                        <div className="mt-1 text-lg font-semibold text-white">
                          {confidenceScore === null
                            ? "Not available"
                            : formatPercent(confidenceScore)}
                        </div>
                      </div>
                    </div>

                    {confidenceLevel === "low" && (
                      <div className="mt-4 flex items-start gap-3 rounded-xl border border-red-900 bg-red-950/40 px-4 py-3 text-sm text-red-200">
                        <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                        <div>
                          Low AI confidence detected. Review flagged items and
                          AI-adjusted rows before sending this quote.
                        </div>
                      </div>
                    )}

                    <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                      <MetricCard label="AI Adjusted Rows" value={String(totalAiAdjustedRows)} />
                      <MetricCard label="Review Flags" value={String(aiReviewFlags.length)} />
                      <MetricCard label="Photo Notes" value={String(photoScopeNotes.length)} />
                      <MetricCard label="Confidence Level" value={confidenceConfig.label} />
                    </div>
                  </div>

                  <div>
                    <h3 className="mb-3 text-sm font-semibold text-white">Materials</h3>
                    <div className="overflow-x-auto rounded-xl border border-gray-800">
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-950">
                          <tr className="text-gray-400">
                            <th className="px-4 py-3 text-left font-medium">Material</th>
                            <th className="px-4 py-3 text-center font-medium">Qty</th>
                            <th className="px-4 py-3 text-center font-medium">Unit</th>
                            <th className="px-4 py-3 text-center font-medium">Unit Price</th>
                            <th className="px-4 py-3 text-center font-medium">Total</th>
                          </tr>
                        </thead>

                        <tbody>
                          {filteredMaterials.length ? (
                            filteredMaterials.map((m, i) => (
                              <tr
                                key={i}
                                className={`border-t text-gray-200 transition hover:bg-gray-900 ${
                                  isAiAdjusted(m)
                                    ? "border-amber-900 bg-amber-950/20"
                                    : "border-gray-800"
                                }`}
                              >
                                <td className="px-4 py-3">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <span>{m.label || m.name || "-"}</span>
                                    {isAiAdjusted(m) && (
                                      <StatusBadge tone="yellow">AI adjusted</StatusBadge>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-center">
                                  {formatNumber(getRowQuantity(m))}
                                </td>
                                <td className="px-4 py-3 text-center">{m.unit || "-"}</td>
                                <td className="px-4 py-3 text-center">
                                  {formatCurrency(getRowUnitPrice(m), selectedCurrency)}
                                </td>
                                <td className="px-4 py-3 text-center">
                                  {formatCurrency(
                                    m.total ?? getRowQuantity(m) * getRowUnitPrice(m),
                                    selectedCurrency
                                  )}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr className="border-t border-gray-800">
                              <td colSpan={5} className="px-4 py-6 text-center text-sm text-gray-500">
                                No material rows to show.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div>
                    <h3 className="mb-3 text-sm font-semibold text-white">Labour</h3>
                    <div className="overflow-x-auto rounded-xl border border-gray-800">
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-950">
                          <tr className="text-gray-400">
                            <th className="px-4 py-3 text-left font-medium">Activity</th>
                            <th className="px-4 py-3 text-center font-medium">Hours</th>
                            <th className="px-4 py-3 text-center font-medium">Rate</th>
                            <th className="px-4 py-3 text-center font-medium">Total</th>
                          </tr>
                        </thead>

                        <tbody>
                          {filteredLabour.length ? (
                            filteredLabour.map((l, i) => (
                              <tr
                                key={i}
                                className={`border-t text-gray-200 transition hover:bg-gray-900 ${
                                  isAiAdjusted(l)
                                    ? "border-amber-900 bg-amber-950/20"
                                    : "border-gray-800"
                                }`}
                              >
                                <td className="px-4 py-3">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <span>{l.label || l.name || "-"}</span>
                                    {isAiAdjusted(l) && (
                                      <StatusBadge tone="yellow">AI adjusted</StatusBadge>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-center">
                                  {formatNumber(getRowQuantity(l))}
                                </td>
                                <td className="px-4 py-3 text-center">
                                  {formatCurrency(getRowUnitPrice(l), selectedCurrency)}
                                </td>
                                <td className="px-4 py-3 text-center">
                                  {formatCurrency(l.total, selectedCurrency)}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr className="border-t border-gray-800">
                              <td colSpan={4} className="px-4 py-6 text-center text-sm text-gray-500">
                                No labour rows to show.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {!!(reviewModeOnly ? filteredExtras.length : result.extras?.length) && (
                    <div>
                      <h3 className="mb-3 text-sm font-semibold text-white">Extras</h3>
                      <div className="overflow-x-auto rounded-xl border border-gray-800">
                        <table className="min-w-full text-sm">
                          <thead className="bg-gray-950">
                            <tr className="text-gray-400">
                              <th className="px-4 py-3 text-left font-medium">Extra</th>
                              <th className="px-4 py-3 text-center font-medium">Total</th>
                            </tr>
                          </thead>

                          <tbody>
                            {filteredExtras.length ? (
                              filteredExtras.map((e, i) => (
                                <tr
                                  key={i}
                                  className={`border-t text-gray-200 transition hover:bg-gray-900 ${
                                    isAiAdjusted(e)
                                      ? "border-amber-900 bg-amber-950/20"
                                      : "border-gray-800"
                                  }`}
                                >
                                  <td className="px-4 py-3">
                                    <div className="flex flex-wrap items-center gap-2">
                                      <span>{e.label || e.name || "-"}</span>
                                      {isAiAdjusted(e) && (
                                        <StatusBadge tone="yellow">AI adjusted</StatusBadge>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    {formatCurrency(e.total, selectedCurrency)}
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr className="border-t border-gray-800">
                                <td colSpan={2} className="px-4 py-6 text-center text-sm text-gray-500">
                                  No extra rows to show.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  <div>
                    <h3 className="mb-3 text-sm font-semibold text-white">Summary</h3>
                    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                      <MetricCard
                        label="Materials"
                        value={formatCurrency(result.summary?.materialsSubtotal, selectedCurrency)}
                      />
                      <MetricCard
                        label="Labour"
                        value={formatCurrency(result.summary?.labourSubtotal, selectedCurrency)}
                      />
                      <MetricCard
                        label="Extras"
                        value={formatCurrency(result.summary?.extrasSubtotal, selectedCurrency)}
                      />
                      <MetricCard
                        label="Base Cost"
                        value={formatCurrency(result.summary?.baseCost, selectedCurrency)}
                      />
                      <MetricCard
                        label={`Margin (${Number(result.summary?.marginPercent ?? marginPercent)}%)`}
                        value={formatCurrency(result.summary?.marginAmount, selectedCurrency)}
                      />
                      <MetricCard
                        label="Subtotal"
                        value={formatCurrency(result.summary?.subtotal, selectedCurrency)}
                      />
                      <MetricCard
                        label="VAT"
                        value={formatCurrency(result.summary?.vat, selectedCurrency)}
                      />
                      <MetricCard
                        label="Total"
                        value={formatCurrency(result.summary?.total, selectedCurrency)}
                        highlight
                      />
                    </div>
                  </div>
                </div>
              </SectionCard>
            )}

            {quoteText && (
              <SectionCard
                title="Quote Draft"
                icon={FileText}
                right={
                  <button
                    onClick={() => navigator.clipboard.writeText(quoteText)}
                    className="inline-flex items-center gap-2 rounded-lg border border-gray-700 bg-gray-900 px-3 py-2 text-sm text-gray-200 transition hover:bg-gray-800"
                  >
                    <Copy size={16} />
                    Copy Quote
                  </button>
                }
              >
                <textarea
                  value={quoteText}
                  onChange={(e) => setQuoteText(e.target.value)}
                  rows={16}
                  className="w-full rounded-xl border border-gray-800 bg-gray-950 px-4 py-3 text-sm text-gray-100 outline-none transition focus:border-gray-700 focus:ring-1 focus:ring-gray-700"
                />
              </SectionCard>
            )}
          </div>
        </div>
      )}
    </div>
  );
}